import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm, Controller } from 'react-hook-form'
import { setModal, selectModal } from '../../../redux/modalSlice'
import { setNotification } from '../../../redux/notificationSlice'
import { triggerRerender } from '../../../redux/rerenderSlice'
import { useRequest } from '../../../hooks/UseRequest'
import { fetchWithTimeoutAndRetry } from '../../../utils/fetchHelper'
import { CurrencyFormat } from '../../../helpers/formatting'

// Self-owned modal for the Resolution quick action. Single binding-amount
// input. On save, drives PaymentPlanOrchestrator::sendToResolution via
// PATCH /admin/loan/{loanId}/resolution.
//
// Re-resolution paths:
//   - Loan in resolution + no_res_plan (thin plan from prior cancelled
//     resolution): the back-end cancel-and-recreate is non-destructive (no
//     scheduled payments to delete). Modal shows current outstanding for
//     context but no extra confirmation gate.
//   - Loan in resolution + resolution_plan (active fleshed plan): the
//     cancel deletes scheduled payments. Modal shows replace-copy and
//     requires an "I understand" confirm tick before Save unlocks.
//
// Detection signal: the current plan's `payment_plan_payments` array.
// Empty (or missing) => thin plan or no plan; non-empty => fleshed plan.
export default function ResolutionAmountModal() {
  const dispatch = useDispatch()
  const modal = useSelector(selectModal)
  const request = useRequest()
  // mode: 'onChange' fires the validate function on every input change so the
  // outstanding-balance warning surfaces as the agent types past the cap,
  // rather than waiting for Save. Initial render has no errors (validation
  // hasn't run yet); first change triggers it.
  const { handleSubmit, control, formState: { errors }, reset, trigger } = useForm({ mode: 'onChange' })

  const [saving, setSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  // outstandingTotal: the loan's true post-discount payoff at $now (includes partial-period interest
  //   when the loan is still accruing). Not used for the bind validation — kept available so the
  //   modal can surface it alongside the binding ceiling if product wants both visible.
  // maxPlanAmount: outstandingTotal minus the partial-period interest projection — the actual
  //   ceiling a new resolution can bind to (partial-period interest never materialises post-transition).
  //   For non-accruing loans these are equal.
  const [outstandingTotal, setOutstandingTotal] = useState(null)
  const [maxPlanAmount, setMaxPlanAmount] = useState(null)
  const [hasActiveFleshedPlan, setHasActiveFleshedPlan] = useState(false)
  const [replaceConfirmed, setReplaceConfirmed] = useState(false)

  const isOpen = modal.is_active && modal.type === 'resolution_amount_modal'
  const loanId = modal.options?.loanId

  // When `maxPlanAmount` lands after the user has already typed a value,
  // re-run validation so the over-cap warning surfaces without requiring a fresh
  // keystroke. Skipped when null/unchanged.
  useEffect(() => {
    if (maxPlanAmount != null) {
      trigger('resolution_amount')
    }
  }, [maxPlanAmount, trigger])

  // Fetch the current plan (if any) when the modal opens. The response also
  // carries both `loan_outstanding_total` (truthful payoff) and
  // `max_plan_amount` (payoff minus partial-period interest — the
  // bind ceiling). See PaymentPlanActionController::showCurrentPaymentPlan
  // for the rationale.
  useEffect(() => {
    let cancelled = false
    if (!isOpen || !loanId) return

    const fetchCurrent = async () => {
      try {
        request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_plan/current`)
        const response = await fetchWithTimeoutAndRetry(() => request.get())
        if (cancelled) return
        if (response?.data?.loan_outstanding_total != null) {
          setOutstandingTotal(response.data.loan_outstanding_total)
        }
        if (response?.data?.max_plan_amount != null) {
          setMaxPlanAmount(response.data.max_plan_amount)
        }
        const planPayments = response?.data?.data?.payment_plan_payments
        if (Array.isArray(planPayments) && planPayments.length > 0) {
          setHasActiveFleshedPlan(true)
        }
      } catch {
        // No current plan is a valid state (first-time resolution); silently fall through.
      }
    }
    fetchCurrent()
    return () => { cancelled = true }
  }, [isOpen, loanId])

  const handleCancel = () => {
    setErrorMessage(null)
    setOutstandingTotal(null)
    setMaxPlanAmount(null)
    setHasActiveFleshedPlan(false)
    setReplaceConfirmed(false)
    reset()
    dispatch(setModal({ is_active: false }))
  }

  const onSubmit = async (formData) => {
    setSaving(true)
    setErrorMessage(null)
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/resolution`)
    try {
      const response = await request.modify({ resolution_amount: parseFloat(formData.resolution_amount) })
      if (response.status >= 200 && response.status < 300) {
        dispatch(setNotification({ type: 'success', body: 'Resolution amount saved' }))
        dispatch(triggerRerender({ subpanelId: 'payment_schedule' }))
        if (typeof modal.options?.successCallback === 'function') {
          modal.options.successCallback()
        }
        handleCancel()
      } else {
        setErrorMessage(`Error: ${response.status}`)
      }
    } catch (error) {
      setErrorMessage(error?.response?.data?.message ?? 'An unexpected error occurred.')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      id="resolution_amount_modal_form"
      className="lox-form"
      data-testid="resolution-amount-modal"
      // noValidate disables the browser's native HTML5 validation so our
      // react-hook-form `validate` function actually runs on submit. Without
      // it, an input value that exceeds the `max` attr (the outstanding
      // balance) is silently blocked by the browser and our custom warning
      // copy never reaches the user. jsdom doesn't implement HTML5 validation
      // so RTL tests pass either way; real browsers do, hence this guard.
      noValidate
    >
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card column is-5">
          <header className="modal-card-head py-3">
            <p className="modal-card-title" data-testid="resolution-amount-modal-title">Resolution</p>
            <button type="button" className="delete" aria-label="close" onClick={handleCancel}></button>
          </header>
          <section className="modal-card-body" style={{ minHeight: '180px' }}>
            <h3 className="title is-6 mb-2" data-testid="resolution-amount-modal-section-title">Resolution Plan</h3>
            <p className="has-text-danger is-size-7 mb-2" data-testid="resolution-amount-modal-tribe-warning">
              ONLY use this form if the tribe has instructed that we settle a Good Standing loan for a given amount. If you need to settle a Good Standing loan for $0, use the Waive Quick Action.
            </p>
            <hr className="my-3" />
            {hasActiveFleshedPlan && (
              <p className="is-size-7 mb-3" data-testid="resolution-amount-modal-replace-copy">
                Replace the active resolution plan with a new one?
              </p>
            )}
            {maxPlanAmount != null && (
              <div className="is-flex is-justify-content-space-between mb-3" data-testid="resolution-amount-modal-outstanding">
                <strong>Max Resolution Amount</strong>
                <strong><CurrencyFormat value={maxPlanAmount} /></strong>
              </div>
            )}
            <div className="field">
              <p className="has-text-weight-bold mb-1">Resolution Settlement Amount</p>
              <label htmlFor="resolution_amount" className="is-size-7">
                Amount ($) <span className="has-text-danger">*</span>
              </label>
              <Controller
                name="resolution_amount"
                control={control}
                defaultValue=""
                rules={{
                  required: 'Resolution amount is required',
                  // Bind ceiling is maxPlanAmount (payoff minus partial-period interest);
                  // the BE precondition matches because sendToResolution deletes future
                  // installments before its plan-create call. Equal-to-max is allowed:
                  // a cancel-and-recreate resolution at the exact owed amount (zero
                  // forgiveness) is a legitimate flow per docs/PAYMENT_PLANS.md.
                  validate: (raw) => {
                    const value = parseFloat(raw)
                    if (Number.isNaN(value)) return 'Must be a number'
                    if (value <= 0) return 'Must be greater than zero'
                    if (value > 99999.99) return 'Must be 99,999.99 or less'
                    if (maxPlanAmount != null && value > maxPlanAmount) {
                      return 'Resolution cannot exceed the max resolution amount'
                    }
                    return true
                  },
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={maxPlanAmount ?? undefined}
                    id="resolution_amount"
                    className="input is-small"
                    data-testid="resolution-amount-modal-input"
                  />
                )}
              />
              <p className="has-text-danger is-size-7 lox-capitalize-first-letter" data-testid="resolution-amount-modal-validation">
                {errors.resolution_amount && <span>{errors.resolution_amount.message}</span>}
              </p>
            </div>
            {hasActiveFleshedPlan && (
              <div className="field" data-testid="resolution-amount-modal-replace-field">
                {/*
                  Flex layout so the wrap-line of the warning sits indented
                  under the text, not under the checkbox box. Without this,
                  the native-sized checkbox + is-size-7 text combination
                  wraps the second line directly below the checkbox in
                  narrow modals, reading as compressed/misaligned.
                  is-flex-shrink-0 keeps the checkbox from squeezing when
                  the text is long.
                */}
                <label className="checkbox is-flex is-align-items-center" style={{ width: '100%' }}>
                  <input
                    type="checkbox"
                    checked={replaceConfirmed}
                    onChange={(e) => setReplaceConfirmed(e.target.checked)}
                    data-testid="resolution-amount-modal-replace-confirm"
                    className="mr-2 is-flex-shrink-0"
                  />
                  <span className="is-size-7">I understand the active plan will be cancelled.</span>
                </label>
              </div>
            )}
            {errorMessage && (
              <p className="has-text-danger is-size-7 mt-2" role="alert" data-testid="resolution-amount-modal-error">{errorMessage}</p>
            )}
            <div className="lox-form__buttons-wrapper has-text-right mt-3">
              <button
                type="submit"
                className="button is-link is-small"
                disabled={saving || (hasActiveFleshedPlan && !replaceConfirmed) ? 'disabled' : undefined}
                data-testid="resolution-amount-modal-save"
              >
                {saving ? 'Saving' : 'Save'}
              </button>
            </div>
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button type="button" className="button is-small has-background-grey-lighter" onClick={handleCancel} data-testid="resolution-amount-modal-close">
                Close
              </button>
            </div>
          </footer>
        </div>
      </div>
    </form>
  )
}
